'use client'

import { CSSProperties, useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { RoomConnectOptions } from 'livekit-client'

import { store } from '@/src/store/rootReducer'
import { useAppSelector } from '@/src/store/hooks'
import { selectInternetState } from '@/src/store/slices/internet'
import { selectRoomState } from '@/src/store/slices/room'
import { selectBranding } from '@/src/store/slices/meetData'
import { selectAccessToken } from '@/src/store/slices/token'

import { useLayoutContext, useMaybeRoomContext } from '@/src/context'
import { LayoutContextProvider } from '@/src/layout/LayoutContextProvider'

import { LiveKitRoom } from '@/src/components/LiveKitRoom'
import { Toast } from '@/src/components/Toast'

import { TabsPanel } from '@/src/components/pip/components/Tabs/TabsPanel/TabsPanel'
import { MainScreen } from '@/src/components/pip/components/MainScreen/MainScreen'

const connectOptions: RoomConnectOptions = {
  autoSubscribe: true,
}

export const pipSizes = {
  width: 270,
  height: 515,
}

export const PictureInPicture = () => {
  const userChoices = useMemo(() => JSON.parse(localStorage.getItem('lk-user-choices') || '{}'), [])

  const layoutContext = useLayoutContext()
  const room = useMaybeRoomContext()
  const { livekit_url } = useAppSelector(selectRoomState)

  const token = useAppSelector(selectAccessToken)

  const { t } = useTranslation()
  const { badInternet } = useAppSelector(selectInternetState)
  const { text_primary_color } = useAppSelector(selectBranding)

  async function openPictureInPicture() {
    const toastStyles: CSSProperties = {
      zIndex: '9999',
      textAlign: 'center',
      color: text_primary_color,
    }

    try {
      // Пока что для ts у window нет такого поля documentPictureInPicture. TODO: Периодически убирать @ts-ignore и смотреть убралась ошибка или нет.
      // @ts-ignore
      const pipWindow = await window.documentPictureInPicture
        .requestWindow(pipSizes)
        .catch((err) => {
          throw new Error(`Error when trying to open the DPiP window: \n${err}`)
        })

      Array.from(document.styleSheets).forEach((styleSheet) => {
        try {
          const cssRules = Array.from(styleSheet.cssRules)
            .map((rule) => rule.cssText)
            .join('')
          const style = document.createElement('style')

          style.textContent = cssRules

          pipWindow.document.head.appendChild(style)
        } catch (error) {
          console.error('Ошибка при задании стилей для Document-Picture-in-Picture окна: \n', error)

          const link = document.createElement('link')

          link.rel = 'stylesheet'
          link.type = styleSheet.type
          link.media = styleSheet.media.toString()
          link.href = styleSheet.href
          pipWindow.document.head.appendChild(link)
        }
      })

      const pipDiv = pipWindow.document.createElement('main')
      pipDiv.setAttribute('id', 'pip-root')
      pipDiv.setAttribute('class', 'pip-root')
      pipWindow.document.body.append(pipDiv)
      const PIP_ROOT = ReactDOM.createRoot(pipWindow.document.getElementById('pip-root'))

      // Так как внутри Picture in Picture у нас создается полностью новое окружение, то мы вынуждены создавать новое React приложение и прокидывать туда все контексты, которые у нас есть для корректной синхронной работы встречи на основной странице и приложения внутри PiP
      PIP_ROOT.render(
        <Provider store={store}>
          <LiveKitRoom
            room={room}
            token={token}
            serverUrl={livekit_url}
            connectOptions={connectOptions}
            video={userChoices?.videoEnabled}
            audio={userChoices?.audioEnabled}
            isPiP
          >
            <LayoutContextProvider value={layoutContext}>
              {badInternet && (
                <Toast
                  style={toastStyles}
                  className="lk-toast-connection-state lk-toast-connection-state lk-toast-internet-state"
                >
                  {t('notStable')}
                </Toast>
              )}

              <TabsPanel />
              <MainScreen />
            </LayoutContextProvider>
          </LiveKitRoom>
        </Provider>,
      )
    } catch (e) {
      console.error(`Произошла ошибка при создании окна Document-Picture-in-Picture: \n`, e)
    }
  }

  const autoOpenPip = () => {
    try {
      navigator.mediaSession.setActionHandler(
        // Пока что enterpictureinpicture не добавили в ts в качестве одного из action-ов mediaSession-а. TODO: Периодически убирать @ts-ignore и смотреть убралась ошибка или нет.
        // @ts-ignore
        'enterpictureinpicture',
        async () => await openPictureInPicture(),
      )
    } catch (e) {
      console.warn(`The browser doesn't support auto opening DPiP window: \n`, e)
    }
  }

  useEffect(() => {
    // Это нужно для того, чтобы при инициализации приложения, мы повесили на событие enterpictureinpicture нашу функцию
    autoOpenPip()
  }, [])

  useEffect(() => {
    // Эта функция чистит все треки, которые были опубликованы внутри PiP
    const clearTracks = () => {
      room?.remoteParticipants?.forEach((participant) => {
        participant?.trackPublications?.forEach((publication) => {
          publication?.track?.attachedElements?.forEach((element) => {
            element?.classList?.forEach((className) => {
              if (className?.includes('pip-track')) {
                publication?.track?.detach(element)
              }
            })
          })
        })
      })
    }

    function handleVisibilitychange(this: Document) {
      if (this?.hidden) {
        autoOpenPip()
      } else {
        // Чистим треки после закрытия PiP
        clearTracks()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilitychange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilitychange)
    }
  }, [room?.remoteParticipants])

  return null
  // return <button onClick={openPictureInPicture}>OPEN</button>
}
